import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FaAngleLeft,
  FaAngleRight,
  FaBuilding,
  FaBuildingColumns,
  FaBoxArchive,
  FaCheck,
  FaChevronDown,
  FaFileInvoice,
  FaFolderOpen,
  FaPlus,
  FaRightFromBracket,
  FaUser,
  FaUsersGear,
  FaBookOpen,
} from "react-icons/fa6";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import Logo from "./Logo";
import { toast } from "react-toastify";

const navigation = [
  { to: "/invoices", label: "Invoice Workspace", icon: FaFileInvoice },
  { to: "/companies", label: "Company Details", icon: FaBuilding },
  { to: "/bank-details", label: "Bank Details", icon: FaBuildingColumns },
  { to: "/sender-profiles", label: "Sender Profiles", icon: FaUser },
  { to: "/documents", label: "Documents", icon: FaFolderOpen, end: true },
  { to: "/team", label: "Team", icon: FaUsersGear, ownerOnly: true },
];

const invoiceNavigation = navigation.filter((item) => !item.ownerOnly);
const assetNavigation = [
  { to: "/assets", label: "Asset Management", icon: FaBoxArchive, end: true },
  { to: "/assets/assignment", label: "Asset Assignment", icon: FaUsersGear },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { orgs, activeOrgId, setActiveOrgId } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const organizationMenuRef = useRef<HTMLDivElement>(null);
  const [organizationMenuOpen, setOrganizationMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("dashboardSidebarCollapsed") === "true"
  );
  const [invoiceMenuOpen, setInvoiceMenuOpen] = useState(
    () => !location.pathname.startsWith("/assets")
  );
  const [collapsedInvoiceMenuOpen, setCollapsedInvoiceMenuOpen] = useState(false);
  const [assetMenuOpen, setAssetMenuOpen] = useState(
    () => location.pathname.startsWith("/assets")
  );
  const [collapsedAssetMenuOpen, setCollapsedAssetMenuOpen] = useState(false);
  const activeOrganization = orgs.find((org) => org.id === activeOrgId);

  useEffect(() => {
    const closeMenu = (event: MouseEvent) => {
      if (
        organizationMenuRef.current &&
        !organizationMenuRef.current.contains(event.target as Node)
      ) {
        setOrganizationMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOrganizationMenuOpen(false);
    };
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  useEffect(() => {
    const assetsActive = location.pathname.startsWith("/assets");
    setAssetMenuOpen(assetsActive);
    setInvoiceMenuOpen(!assetsActive);
  }, [location.pathname]);

  const toggleSidebar = () => {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem("dashboardSidebarCollapsed", String(next));
      return next;
    });
  };

  const handleLogout = async () => {
    await logout();
    toast.success("You have been logged out.");
    navigate("/login", { replace: true });
  };

  const handleOrgChange = (value: string) => {
    setOrganizationMenuOpen(false);
    if (value === "__new__") {
      navigate("/select-organization");
      return;
    }
    setActiveOrgId(Number(value));
    const organization = orgs.find((org) => org.id === Number(value));
    if (organization) toast.success(`Switched to ${organization.name}.`);
  };

  return (
    <div className="flex min-h-screen bg-slate-50/40">
      <aside
        className={`sticky top-0 z-30 flex h-screen flex-none flex-col border-r border-slate-200/80 bg-white/90 shadow-[8px_0_35px_-24px_rgba(15,23,42,.35)] backdrop-blur-xl transition-[width] duration-300 ${
          collapsed ? "w-20" : "w-64"
        }`}
      >
        <div className={`relative flex h-20 items-center border-b border-slate-100 ${collapsed ? "justify-center px-3" : "px-5"}`}>
          <a
            href="https://getvak.com"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visit GetVAK"
            className={`group flex items-center rounded-lg outline-none transition hover:opacity-80 focus-visible:ring-2 focus-visible:ring-teal-500 ${
              collapsed ? "w-10 justify-center" : "w-36"
            }`}
          >
            <img
              src="https://getvak.com/images/brand/logo.png"
              alt="GetVAK"
              className={`object-contain ${collapsed ? "h-7 w-10" : "h-9 w-32 object-left"}`}
            />
            {collapsed && (
              <span
                role="tooltip"
                className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 z-50 -translate-y-1/2 translate-x-1 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-xl transition duration-150 group-hover:translate-x-0 group-hover:opacity-100"
              >
                Visit GetVAK
                <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
              </span>
            )}
          </a>
          <button
            type="button"
            onClick={toggleSidebar}
            title={collapsed ? "Expand navigation" : "Collapse navigation"}
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            className={`absolute flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700 ${
              collapsed ? "-right-4" : "right-4"
            }`}
          >
            {collapsed ? <FaAngleRight /> : <FaAngleLeft />}
          </button>
        </div>

        <nav className="flex-1 space-y-1.5 px-3 py-6">
          {!collapsed && (
            <p className="mb-3 px-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
              Workspace
            </p>
          )}
          <div
            className="relative space-y-1"
            onMouseEnter={() => { if (collapsed) { setCollapsedInvoiceMenuOpen(true); setCollapsedAssetMenuOpen(false); } }}
            onMouseLeave={() => { if (collapsed) setCollapsedInvoiceMenuOpen(false); }}
          >
            <button
              type="button"
              onClick={() => collapsed ? setCollapsedInvoiceMenuOpen(true) : setInvoiceMenuOpen((open) => !open)}
              aria-expanded={collapsed ? collapsedInvoiceMenuOpen : invoiceMenuOpen}
              className={`flex h-12 w-full items-center rounded-xl text-sm font-semibold text-slate-700 transition hover:bg-teal-50 hover:text-teal-800 ${collapsed ? "justify-center px-3" : "gap-3 px-3"}`}
              title="Invoicing"
            >
              <FaFileInvoice className="h-[18px] w-[18px] flex-none" />
              {!collapsed && <><span className="flex-1 text-left">Invoicing</span><FaChevronDown className={`h-3 w-3 transition ${invoiceMenuOpen ? "rotate-180" : ""}`} /></>}
            </button>
            {(!collapsed && invoiceMenuOpen) && <div className="ml-4 space-y-1 border-l border-slate-200 pl-2">
              {invoiceNavigation.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-semibold transition ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}><Icon className="h-4 w-4" />{label}</NavLink>)}
            </div>}
            {collapsed && collapsedInvoiceMenuOpen && <div className="absolute left-full top-0 z-50 w-56 rounded-r-2xl border border-slate-200 bg-white p-2 shadow-2xl">
              <p className="px-3 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[.16em] text-slate-400">Invoicing</p>
              {invoiceNavigation.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setCollapsedInvoiceMenuOpen(false)} className={({ isActive }) => `flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"}`}><Icon className="h-4 w-4" />{label}</NavLink>)}
            </div>}
          </div>
          <div
            className="relative space-y-1"
            onMouseEnter={() => { if (collapsed) { setCollapsedAssetMenuOpen(true); setCollapsedInvoiceMenuOpen(false); } }}
            onMouseLeave={() => { if (collapsed) setCollapsedAssetMenuOpen(false); }}
          >
            <button type="button" onClick={() => collapsed ? setCollapsedAssetMenuOpen(true) : setAssetMenuOpen((open) => !open)} aria-expanded={collapsed ? collapsedAssetMenuOpen : assetMenuOpen} className={`flex h-12 w-full items-center rounded-xl text-sm font-semibold text-slate-700 transition hover:bg-teal-50 hover:text-teal-800 ${collapsed ? "justify-center px-3" : "gap-3 px-3"}`} title="Asset Management">
              <FaBoxArchive className="h-[18px] w-[18px] flex-none" />
              {!collapsed && <><span className="flex-1 text-left">Asset Management</span><FaChevronDown className={`h-3 w-3 transition ${assetMenuOpen ? "rotate-180" : ""}`} /></>}
            </button>
            {(!collapsed && assetMenuOpen) && <div className="ml-4 space-y-1 border-l border-slate-200 pl-2">{assetNavigation.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex h-10 items-center gap-3 rounded-lg px-3 text-sm font-semibold transition ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"}`}><Icon className="h-4 w-4" />{label}</NavLink>)}</div>}
            {collapsed && collapsedAssetMenuOpen && <div className="absolute left-full top-0 z-50 w-56 rounded-r-2xl border border-slate-200 bg-white p-2 shadow-2xl"><p className="px-3 pb-2 pt-1 text-[10px] font-bold uppercase tracking-[.16em] text-slate-400">Asset Management</p>{assetNavigation.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setCollapsedAssetMenuOpen(false)} className={({ isActive }) => `flex h-10 items-center gap-3 rounded-xl px-3 text-sm font-semibold transition ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800"}`}><Icon className="h-4 w-4" />{label}</NavLink>)}</div>}
          </div>
          {activeOrganization?.role === "owner" && <NavLink to="/team" className={({ isActive }) => `flex h-12 items-center rounded-xl text-sm font-semibold transition ${collapsed ? "justify-center px-3" : "gap-3 px-3"} ${isActive ? "bg-teal-700 text-white shadow-md shadow-teal-900/15" : "text-slate-600 hover:bg-teal-50 hover:text-teal-800"}`}><FaUsersGear className="h-[18px] w-[18px]" />{!collapsed && <span>Team</span>}</NavLink>}
        </nav>

        <div className="border-t border-slate-100 p-3">
          <NavLink
            to="/docs"
            className={`group relative mb-1 flex h-11 items-center rounded-xl text-sm font-semibold text-slate-500 transition hover:bg-teal-50 hover:text-teal-800 ${
              collapsed ? "justify-center px-3" : "gap-3 px-3"
            }`}
          >
            <FaBookOpen className="h-[17px] w-[17px] flex-none" aria-hidden="true" />
            {!collapsed && <span>Product Docs</span>}
            {collapsed && (
              <span role="tooltip" className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-xl transition group-hover:opacity-100">
                Product Docs
              </span>
            )}
          </NavLink>
          <div
            className={`group relative mb-1 flex min-h-14 items-center rounded-xl bg-slate-50 ${
              collapsed ? "justify-center px-2" : "gap-3 px-3 py-2"
            }`}
          >
            {user?.image ? (
              <img
                src={user.image}
                alt={user?.name || user?.email || "User"}
                referrerPolicy="no-referrer"
                className="h-9 w-9 flex-none rounded-xl object-cover"
              />
            ) : (
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-teal-100 font-bold text-teal-700">
                {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
              </span>
            )}
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-700">
                  {user?.name || user?.email}
                </p>
                {user?.name && <p className="truncate text-xs text-slate-400">{user.email}</p>}
              </div>
            )}
            {collapsed && (
              <span
                role="tooltip"
                className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 z-50 -translate-y-1/2 translate-x-1 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-xl transition duration-150 group-hover:translate-x-0 group-hover:opacity-100"
              >
                {user?.name || user?.email}
                <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleLogout}
            aria-label="Log out"
            className={`group relative flex h-12 w-full items-center rounded-xl text-sm font-semibold text-slate-500 transition hover:bg-red-50 hover:text-red-600 ${
              collapsed ? "justify-center px-3" : "gap-3 px-3"
            }`}
          >
            <FaRightFromBracket className="h-[18px] w-[18px] flex-none" />
            {!collapsed && <span>Log out</span>}
            {collapsed && (
              <span
                role="tooltip"
                className="pointer-events-none absolute left-[calc(100%+12px)] top-1/2 z-50 -translate-y-1/2 translate-x-1 whitespace-nowrap rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-xl transition duration-150 group-hover:translate-x-0 group-hover:opacity-100"
              >
                Log out
                <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
              </span>
            )}
          </button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/75 px-4 py-4 backdrop-blur-xl sm:px-6">
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-4">
              <Logo className="hidden text-xl text-slate-900 sm:inline-flex" />
            </div>

            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <div ref={organizationMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setOrganizationMenuOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={organizationMenuOpen}
                  className={`flex min-w-44 max-w-56 items-center gap-3 rounded-xl border bg-white px-2.5 py-2 text-left shadow-sm outline-none transition sm:min-w-56 ${
                    organizationMenuOpen
                      ? "border-teal-400 ring-4 ring-teal-500/10"
                      : "border-slate-200 hover:border-teal-300"
                  }`}
                >
                  <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-teal-100 text-xs font-bold uppercase text-teal-700">
                    {(activeOrganization?.name || "IS").slice(0, 2)}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                      Organization
                    </span>
                    <span className="block truncate text-sm font-semibold text-slate-700">
                      {activeOrganization?.name || "Select organization"}
                    </span>
                  </span>
                  <FaChevronDown
                    className={`h-3 w-3 flex-none text-slate-400 transition-transform ${
                      organizationMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {organizationMenuOpen && (
                  <div
                    role="listbox"
                    aria-label="Organizations"
                    className="absolute right-0 top-[calc(100%+0.55rem)] z-50 w-72 overflow-hidden rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_20px_55px_-18px_rgba(15,23,42,.35)]"
                  >
                    <div className="px-2 pb-2 pt-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                        Switch workspace
                      </p>
                    </div>
                    <div className="max-h-72 space-y-1 overflow-y-auto">
                      {orgs.map((org) => {
                        const selected = org.id === activeOrgId;
                        return (
                          <button
                            key={org.id}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => handleOrgChange(String(org.id))}
                            className={`flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition ${
                              selected
                                ? "bg-teal-50 text-teal-900"
                                : "text-slate-700 hover:bg-slate-50"
                            }`}
                          >
                            <span
                              className={`flex h-10 w-10 flex-none items-center justify-center rounded-xl text-xs font-bold uppercase ${
                                selected
                                  ? "bg-teal-700 text-white"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              {org.name.slice(0, 2)}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold">{org.name}</span>
                              <span className="block text-xs capitalize text-slate-400">
                                {org.role || "member"} workspace
                              </span>
                            </span>
                            {selected && (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-100 text-teal-700">
                                <FaCheck className="h-3 w-3" />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-2 border-t border-slate-100 pt-2">
                      <button
                        type="button"
                        onClick={() => handleOrgChange("__new__")}
                        className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left text-teal-700 transition hover:bg-teal-50"
                      >
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-dashed border-teal-300 bg-teal-50">
                          <FaPlus className="h-3.5 w-3.5" />
                        </span>
                        <span>
                          <span className="block text-sm font-semibold">New organization</span>
                          <span className="block text-xs text-teal-600/70">Create another workspace</span>
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 py-7 sm:px-6 sm:py-9">
          <div key={activeOrgId} className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
